<script setup lang="ts">
import { uniqBy } from 'lodash'
import { $lineItemAreaList } from '@/api/brandings'

interface IProps {
  modelValue?: any[],
  label?: string,
  selectOptions?: any[] | null,
  /**
   * Optional array of itemArea IDs that should be disabled for selection.
   * If null or undefined, no itemAreas will be disabled.
   */
  disabledItemAreas?: number[] | null,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: () => ([]),
  label: 'Area',
  selectOptions: null,
  disabledItemAreas: null
})

interface CustomEvents {
  'update:modelValue': (newValue: any) => void;
}

const emit = defineEmits<CustomEvents>()

const itemAreas = ref<string[]>([])
const itemAreasCurrentPage = ref<number>(1)
const itemAreasPerPage: number = 10
const itemAreasSearchStr = ref<string>('')
const isNextItemAreasPageExist = ref<boolean>(false)
const itemAreasLoading = ref<boolean>(false)

const itemAreaOptions = computed(() => {
  if (props.selectOptions) {
    return props.selectOptions
  } else {
    return itemAreas.value
  }
})

const getItemAreas = async () => {
  itemAreasLoading.value = true
  try {
    const res = await $lineItemAreaList({
      search: itemAreasSearchStr.value,
      per_page: itemAreasPerPage,
      page: itemAreasCurrentPage.value
    })

    // Append the new itemAreas to the existing list
    // Use Lodash's uniqBy to merge arrays while ensuring uniqueness based on 'id'
    itemAreas.value = uniqBy([...itemAreas.value, ...res.data.results], 'id')
    isNextItemAreasPageExist.value = res.data.next
  } catch (e) {
    console.log(e)
  } finally {
    itemAreasLoading.value = false
  }
}

const onFocusItemAreaSelect = async () => {
  if (!itemAreas.value.length && !props.selectOptions) {
    itemAreas.value = []
    itemAreasCurrentPage.value = 1
    await getItemAreas()
  }
}

const onLoadMoreItemAreas = async () => {
  itemAreasCurrentPage.value++ // Increment the current page to load the next page of itemAreas
  await getItemAreas()
}

const clearAndGetItemAreas = (searchStr: string) => {
  itemAreas.value = []
  itemAreasCurrentPage.value = 1 // Set current page => 1 to load initial first page of itemAreas
  itemAreasSearchStr.value = searchStr
  getItemAreas()
}

const handleItemAreaSearch = useDebounceFn((searchStr: string) => {
  if (!props.selectOptions) {
    clearAndGetItemAreas(searchStr)
  }
}, 500)

defineExpose({ clearAndGetItemAreas })
</script>

<template>
  <VAutocomplete
    :model-value="modelValue"
    :items="[...itemAreaOptions]"
    :label="label"
    density="compact"
    :no-filter="!selectOptions"
    multiple
    clearable
    @update:modelValue="newValue => $emit('update:modelValue', newValue)"
    @update:focused="onFocusItemAreaSelect"
    @update:search="handleItemAreaSearch"
  >
    <template
      v-if="disabledItemAreas"
      #item="{ item, index, props }"
    >
      <VListItem
        v-bind="props"
        :disabled="!!disabledItemAreas ? disabledItemAreas.includes(item?.value) : false"
      />
    </template>
    <template #selection="{ item, index }">
      <div class="d-flex align-center">
        <v-chip v-if="index < 1">
          <span>{{ item.title }}</span>
        </v-chip>
        <span
          v-if="index === 1"
          class="text-grey text-caption align-self-center"
        >
          (+{{ modelValue?.length - 1 }} others)
        </span>
      </div>
    </template>
    <template #no-data>
      <div
        v-if="!!itemAreasLoading"
        class="d-flex justify-center align-center"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </div>
      <div
        v-else
        class="d-flex justify-center align-center"
      >
        Areas not found
      </div>
    </template>
    <template
      v-if="isNextItemAreasPageExist"
      #append-item
    >
      <VBtn
        color="default"
        block
        :disabled="!!itemAreasLoading"
        @click="onLoadMoreItemAreas"
      >
        Load more...
        <VProgressCircular
          v-if="!!itemAreasLoading"
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </VBtn>
    </template>
  </VAutocomplete>
</template>

<style lang="scss">

</style>
